/**
 * Renders a screen reader-only alert message that can be forced to re-announce
 * itself via the `forceAlert` method on the ref. This is useful when you want
 * to be able to force a screen reader to re-read a message that has not
 * changed.
 */

import * as React from "react";

export type Props = {
    children: React.ReactNode;
};

export interface ScreenReaderAlertRefHandle {
    forceAlert: () => void;
}

function ScreenReaderAlert(
    {children}: Props,
    ref: React.Ref<ScreenReaderAlertRefHandle>,
): React.ReactElement {
    const [numForceAlerts, setNumForceAlerts] = React.useState(0);

    React.useImperativeHandle(ref, () => ({
        forceAlert: () => {
            setNumForceAlerts((num) => num + 1);
        },
    }));

    return (
        <div
            role="alert"
            key={numForceAlerts}
            data-force-alert-key={numForceAlerts}
            className="sr-only"
        >
            {children}
        </div>
    );
}

export default React.forwardRef(ScreenReaderAlert);
